/* ==========================================================================
   MAIN PRODUCT TRAVEL SYSTEM — STUDIOIB | MINIBEE  ·  v2
   ──────────────────────────────────────────────────────────────────────────
   Cambios v2:
   - Cards minimalistas (solo imagen)
   - Tooltip singleton al hover/touch para mostrar nombre del producto
   - Modal "más info" para sillas con descripción
   - Stepper sin paso "adaptadores" (era buggy y redundante)
   - Resumen con thumbnails + widget de cuotas (Ventipay 3x / MercadoPago 6x)
   - Integración nativa con el drawer del tema (cart-notification)
     usando el patrón de routes.cart_add_url + fetchConfig() + sections
   ========================================================================== */

(function () {
  'use strict';

  /* ─────────── Utilidades ─────────── */

  const formatPrice = (cents) =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format((cents || 0) / 100);

  const els = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const make = (tag, opts = {}) => {
    const node = document.createElement(tag);
    if (opts.className) node.className = opts.className;
    if (opts.html != null) node.innerHTML = opts.html;
    if (opts.text != null) node.textContent = opts.text;
    if (opts.attrs) {
      for (const [k, v] of Object.entries(opts.attrs)) {
        if (v == null || v === false) continue;
        node.setAttribute(k, v === true ? '' : String(v));
      }
    }
    return node;
  };

  const scrollToNext = (slotName) => {
    const next = document.querySelector(`[data-mbts-slot="${slotName}"]`);
    if (!next) return;
    const top = next.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  /* ═══════════════════════════════════════════
     TOOLTIP — singleton flotante con flecha
     Adaptado de more-colors-tooltip.js
     ═══════════════════════════════════════════ */

  const MbtsTooltip = (() => {
    let toast = null;
    let hideTimer = null;

    function ensure() {
      if (toast) return toast;
      toast = document.createElement('div');
      toast.className = 'mbts-tooltip';
      toast.innerHTML =
        '<span class="mbts-tooltip__arrow"></span>' +
        '<span class="mbts-tooltip__text"></span>';
      document.body.appendChild(toast);
      return toast;
    }

    function position(target) {
      const rect = target.getBoundingClientRect();
      const toastWidth = 180;
      const gap = 8;
      let left = rect.left + rect.width / 2 - toastWidth / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - toastWidth - 8));

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow >= 60 || spaceBelow >= spaceAbove) {
        toast.style.top = rect.bottom + gap + window.scrollY + 'px';
        toast.classList.remove('mbts-tooltip--top');
        toast.classList.add('mbts-tooltip--bottom');
      } else {
        toast.style.top = rect.top + window.scrollY - 46 + 'px';
        toast.classList.remove('mbts-tooltip--bottom');
        toast.classList.add('mbts-tooltip--top');
      }
      toast.style.left = left + 'px';
      toast.style.width = toastWidth + 'px';
      toast.style.position = 'absolute';
    }

    function show(target, text) {
      ensure();
      clearTimeout(hideTimer);
      toast.querySelector('.mbts-tooltip__text').textContent = text;
      position(target);
      toast.classList.add('is-visible');
    }

    function hide(delay) {
      if (!toast) return;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => toast.classList.remove('is-visible'), delay || 80);
    }

    return { show, hide };
  })();

  /* ═══════════════════════════════════════════
     MODAL — singleton de descripción
     ═══════════════════════════════════════════ */

  const MbtsModal = (() => {
    let scrollY = 0;
    const isIOS = /iP(ad|hone|od)/.test(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);    
    let closeTimer = null;
    let overlay = null;

    function build() {
      if (overlay) return overlay;
      overlay = document.createElement('div');
      overlay.className = 'mbts-modal-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML = `
        <div class="mbts-modal" role="dialog" aria-modal="true" aria-labelledby="mbts-modal-title">
          <button class="mbts-modal__close" type="button" aria-label="Cerrar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="mbts-modal__media"></div>
          <div class="mbts-modal__body">
            <h3 class="mbts-modal__title" id="mbts-modal-title"></h3>
            <span class="mbts-modal__price"></span>
            <div class="mbts-modal__desc"></div>
            <button class="mbts-modal__select" type="button"></button>
          </div>
        </div>`;
      document.body.appendChild(overlay);

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
      });
      overlay.querySelector('.mbts-modal__close').addEventListener('click', close);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
      });
      return overlay;
    }

    function open({ image, title, description, price, isSelected, onSelect }) {
      build();
      clearTimeout(closeTimer);

      const mediaEl  = overlay.querySelector('.mbts-modal__media');
      const titleEl  = overlay.querySelector('.mbts-modal__title');
      const priceEl  = overlay.querySelector('.mbts-modal__price');
      const descEl   = overlay.querySelector('.mbts-modal__desc');
      const selectEl = overlay.querySelector('.mbts-modal__select');

      mediaEl.innerHTML = image
        ? `<img src="${image}" alt="${title || ''}" draggable="false" />`
        : '';
      if (image) {
        const modalImg = mediaEl.querySelector('img');
        if (modalImg) modalImg.addEventListener('contextmenu', (e) => e.preventDefault());
      }
      titleEl.textContent = title || '';
      if (priceEl) {
        priceEl.textContent = price != null
          ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format((price || 0) / 100)
          : '';
      }
      descEl.textContent = description || '';
      if (selectEl) {
        const selected = !!isSelected;
        selectEl.textContent = selected ? '✓ Seleccionada' : 'Elegir esta silla';
        selectEl.classList.toggle('is-selected', selected);
        selectEl.disabled = selected;
        // Reemplaza listener anterior clonando el nodo
        const freshBtn = selectEl.cloneNode(true);
        selectEl.parentNode.replaceChild(freshBtn, selectEl);
        if (!selected && typeof onSelect === 'function') {
          freshBtn.addEventListener('click', () => onSelect());
        }
      }
    
      overlay.setAttribute('aria-hidden', 'false');
    
      // fuerza el primer paint para que la primera apertura también anime
      overlay.classList.remove('is-open');
      overlay.getBoundingClientRect();
    
      requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        lockScroll();
      });
    }

    function close() {
      if (!overlay) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    
      closeTimer = setTimeout(() => {
        unlockScroll();
      }, 300);
    }

    function lockScroll() {
      scrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('mbts-modal-open');
    
      if (isIOS) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }
    }
    
    function unlockScroll() {
      document.documentElement.classList.remove('mbts-modal-open');
    
      if (isIOS) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      }
    }  

    return { open, close };
  })();

  /* ═══════════════════════════════════════════
     CLASE PRINCIPAL
     ═══════════════════════════════════════════ */

  class MinibeeTravelSystem {
    constructor(rootEl) {
      this.root = rootEl;

      const dataEl = rootEl.querySelector('script[type="application/json"][id^="mbts-data"]');
      if (!dataEl) {
        console.warn('[Minibee TS] No se encontró el script JSON de configuración.');
        return;
      }

      try {
        this.cfg = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[Minibee TS] JSON inválido:', e);
        return;
      }

      this.state = {
        coche: null,
        silla: null,
        adaptadores: [],
        accesorios: [],
        selectedCocheProduct: null,
      };

      this.completedSteps = new Set();
      this.init();
    }

    init() {
      this.renderCoches();
      this.renderSillas('sillas',    '#mbts-grid-sillas',     this.cfg.slots.sillas);
      this.renderSillas('sillasOpt', '#mbts-grid-sillas-opt', this.cfg.slots.sillasOpt);
      this.renderAdaptadores();
      this.renderAccesorios();
      this.bindCTA();
      this.updateSummary();
      this.bindStepperClicks();
      this.bindScrollObserver();

      // Bloquear clic derecho en imágenes del resumen
      const summaryEl = this.root.querySelector('#mbts-summary');
      if (summaryEl) {
        summaryEl.addEventListener('contextmenu', (e) => {
          if (e.target.tagName === 'IMG') e.preventDefault();
        });
      }

      // Inyectar el toast de completado dentro del stepper-wrap
      const stepperWrap = this.root.querySelector('.mbts-stepper-wrap');
      if (stepperWrap) {
        const doneToast = make('div', {
          className: 'mbts-stepper-done-toast',
          html: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Ir al resumen',
          // html: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Travel System listo · Ir al resumen',
        });
        doneToast.addEventListener('click', () => {
          const summary = this.root.querySelector('#mbts-summary');
          if (!summary) return;
          const sw = this.root.querySelector('.mbts-stepper-wrap');
          const offset = sw ? sw.getBoundingClientRect().height + 16 : 130;
          const top = summary.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        stepperWrap.appendChild(doneToast);
      }

      requestAnimationFrame(() => this.root.classList.remove('opacity-0'));
    }

    /* ─── COCHES ─── */

    renderCoches() {
      const grid = this.root.querySelector('#mbts-grid-coches');
      if (!grid) return;

      if (!this.cfg.slots.coches.length) {
        grid.appendChild(make('div', {
          className: 'mbts-card mbts-card--empty',
          text: 'Sin stock',
        }));
        this.updateCurrentLabel('coches', null, null, 'Sin stock disponible por ahora');
        return;
      }

      this.cfg.slots.coches.forEach((product) => {
        const variant = product.variants.find((v) => v.available) || product.variants[0];

        const card = make('button', {
          className: 'mbts-card mbts-card--coche',
          attrs: {
            type: 'button',
            role: 'radio',
            'aria-checked': 'false',
            'aria-label': product.title,
            'data-mbts-product-id': product.id,
            'data-mbts-variant-id': variant.id,
          },
        });

        card.appendChild(this.makeMedia(product.image, product.title));
        card.appendChild(this.makeCheck());
        this.bindCardTooltip(card, product.title);

        card.addEventListener('click', () => this.selectCoche(card, product));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectCoche(card, product);
          }
        });

        grid.appendChild(card);
      });
    }

    selectCoche(card, product) {
      els('.mbts-card--coche', this.root).forEach((c) => {
        c.classList.remove('is-selected', 'is-just-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected', 'is-just-selected');
      card.setAttribute('aria-checked', 'true');
      setTimeout(() => card.classList.remove('is-just-selected'), 500);

      this.state.selectedCocheProduct = product;
      const defaultVariant = product.variants.find((v) => v.available) || product.variants[0];

      this.state.coche = {
        variantId: defaultVariant.id,
        price: defaultVariant.price,
        compareAtPrice: defaultVariant.compareAtPrice,
        title: defaultVariant.title === 'Default Title'
          ? product.title
          : `${product.title} — ${defaultVariant.title}`,
        productTitle: product.title,
        optionTitle: defaultVariant.title === 'Default Title' ? null : defaultVariant.title,
        image: defaultVariant.image || product.image,
      };

      this.markStep('coches');
      this.updateCurrentLabel('coches', this.state.coche.title, defaultVariant.price);
      this.renderCocheVariantPicker(product);
      this.updateSummary();

      //if (window.innerWidth < 768) scrollToNext('sillas');
    }

    renderCocheVariantPicker(product) {
      const wrap  = this.root.querySelector('#mbts-variant-coches');
      const pills = this.root.querySelector('#mbts-variant-pills-coches');
      const lbl   = this.root.querySelector('#mbts-variant-lbl-coches');
      if (!wrap || !pills) return;

      pills.innerHTML = '';

      const hasMultiple = product.variants.length > 1;
      if (!hasMultiple) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;

      const firstOption = product.options && product.options[0];
      lbl.textContent = firstOption && firstOption.name
        ? `Elige ${firstOption.name.toLowerCase()}:`
        : 'Elige variante:';

      product.variants.forEach((variant) => {
        const pill = make('button', {
          className: 'mbts-variant__pill',
          text: variant.title,
          attrs: {
            type: 'button',
            'data-mbts-variant-id': variant.id,
            'aria-pressed': 'false',
            disabled: !variant.available ? '' : null,
          },
        });
        if (variant.id === this.state.coche.variantId) {
          pill.classList.add('is-selected');
          pill.setAttribute('aria-pressed', 'true');
        }

        pill.addEventListener('click', () => {
          els('.mbts-variant__pill', pills).forEach((p) => {
            p.classList.remove('is-selected');
            p.setAttribute('aria-pressed', 'false');
          });
          pill.classList.add('is-selected');
          pill.setAttribute('aria-pressed', 'true');

          this.state.coche.variantId      = variant.id;
          this.state.coche.price          = variant.price;
          this.state.coche.compareAtPrice = variant.compareAtPrice;
          this.state.coche.optionTitle    = variant.title;
          this.state.coche.title          = `${product.title} — ${variant.title}`;
          if (variant.image) this.state.coche.image = variant.image;

          this.updateCurrentLabel('coches', this.state.coche.title, variant.price);
          this.updateSummary();
        });

        pills.appendChild(pill);
      });
    }

    /* ─── SILLAS (con modal de descripción) ─── */

    renderSillas(slotName, gridSelector, products) {
      const grid = this.root.querySelector(gridSelector);
      if (!grid || !products || !products.length) return;

      products.forEach((product) => {
        const variant = product.variants.find((v) => v.available) || product.variants[0];

        const card = make('button', {
          className: 'mbts-card mbts-card--silla',
          attrs: {
            type: 'button',
            role: 'radio',
            'aria-checked': 'false',
            'aria-label': product.title,
            'data-mbts-slot': slotName,
            'data-mbts-variant-id': variant.id,
          },
        });

        card.appendChild(this.makeMedia(product.image, product.title));
        card.appendChild(this.makeCheck());

        // Botón "Ver detalles" solo si hay descripción significativa
        // Se coloca FUERA del card, debajo, en un wrapper
        const wrap = make('div', { className: 'mbts-card-wrap' });
        wrap.appendChild(card);

        if (product.description && product.description.trim().length > 30) {
          const info = make('button', {
            className: 'mbts-card__info',
            html: '<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 11v5M12 8h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg><span>Ver detalles</span>',
            attrs: {
              type: 'button',
              'aria-label': `Más información sobre ${product.title}`,
            },
          });
          info.addEventListener('click', () => {
            MbtsModal.open({
              image: product.image,
              title: product.title,
              description: product.description,
              price: variant.price,
              isSelected: this.state.silla?.variantId === variant.id,
              onSelect: () => {
                this.selectSilla(card, product, variant, slotName);
                MbtsModal.close();
              },
            });
          });
          wrap.appendChild(info);
        }

        grid.appendChild(wrap);

        this.bindCardTooltip(card, product.title);

        card.addEventListener('click', () => this.selectSilla(card, product, variant, slotName));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectSilla(card, product, variant, slotName);
          }
        });
      });
    }

    selectSilla(card, product, variant, slotName) {
      els('.mbts-card--silla', this.root).forEach((c) => {
        c.classList.remove('is-selected', 'is-just-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected', 'is-just-selected');
      card.setAttribute('aria-checked', 'true');
      setTimeout(() => card.classList.remove('is-just-selected'), 500);

      this.state.silla = {
        variantId: variant.id,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        title: product.title,
        image: product.image,
        fromSlot: slotName,
      };

      this.markStep('sillas');
      this.updateCurrentLabel('sillas', product.title, variant.price);
      const altSlot = slotName === 'sillas' ? 'sillasOpt' : 'sillas';
      this.updateCurrentLabel(altSlot, null, null);
      this.updateSummary();

      // if (window.innerWidth < 768) {
      //   if (this.cfg.slots.accesorios.length) scrollToNext('accesorios');
      // }
    }

    /* ─── ADAPTADORES (auto-seleccionados, sin paso de stepper) ─── */

    renderAdaptadores() {
      const grid = this.root.querySelector('#mbts-grid-adaptadores');
      if (!grid || !this.cfg.slots.adaptadores.length) return;

      this.cfg.slots.adaptadores.forEach((product) => {
        const variant = product.variants[0];

        this.state.adaptadores.push({
          variantId: variant.id,
          price: this.cfg.adaptadorGratis ? 0 : variant.price,
          compareAtPrice: variant.price,
          title: product.title,
          image: product.image,
          isGift: !!this.cfg.adaptadorGratis,
        });

        const card = make('div', {
          className: 'mbts-card mbts-card--gift is-selected',
        });

        card.appendChild(this.makeMedia(product.image, product.title));
        card.appendChild(this.makeCheck());
        this.bindCardTooltip(card, product.title);

        if (this.cfg.adaptadorGratis) {
          card.appendChild(make('span', {
            className: 'mbts-card__gift-pill',
            text: 'Gratis',
          }));
        }

        grid.appendChild(card);
      });
    }

    /* ─── ACCESORIOS (multi-selección, layout horizontal con info) ─── */

    renderAccesorios() {
      const grid = this.root.querySelector('#mbts-grid-accesorios');
      if (!grid || !this.cfg.slots.accesorios.length) return;

      this.cfg.slots.accesorios.forEach((product) => {
        const variant = product.variants[0];

        const card = make('div', {
          className: 'mbts-card mbts-card--accesorio',
          attrs: { 'data-mbts-variant-id': variant.id },
        });

        card.appendChild(this.makeMedia(product.image, product.title));

        const body = make('div', { className: 'mbts-card__body' });
        const nameEl = make('a', {
          className: 'mbts-card__name',
          text: product.title,
          attrs: { href: product.url, target: '_blank', rel: 'noopener' },
        });
        nameEl.addEventListener('click', (e) => e.stopPropagation());
        body.appendChild(nameEl);
        body.appendChild(this.makePrice(variant.price, variant.compareAtPrice));
        card.appendChild(body);

        const svgPlus  = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
        const svgCheck = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

        const btn = make('button', {
          className: 'mbts-card__toggle',
          html: svgPlus,
          attrs: {
            type: 'button',
            'aria-pressed': 'false',
            'aria-label': `Agregar ${product.title}`,
          },
        });
        card.appendChild(btn);

        const toggle = () => {
          const idx = this.state.accesorios.findIndex((a) => a.variantId === variant.id);
          if (idx === -1) {
            this.state.accesorios.push({
              variantId: variant.id,
              price: variant.price,
              compareAtPrice: variant.compareAtPrice,
              title: product.title,
              image: product.image,
            });
            card.classList.add('is-selected');
            btn.innerHTML = svgCheck;
            btn.setAttribute('aria-pressed', 'true');
            btn.setAttribute('aria-label', `Quitar ${product.title}`);

            // Animación "Agregado"
            const badge = make('span', {
              className: 'mbts-acc-added-badge',
              text: '✓ Agregado',
            });
            card.appendChild(badge);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => badge.classList.add('is-visible'));
            });
            setTimeout(() => {
              badge.classList.remove('is-visible');
              setTimeout(() => badge.remove(), 350);
            }, 1800);
          } else {
            this.state.accesorios.splice(idx, 1);
            card.classList.remove('is-selected');
            btn.innerHTML = svgPlus;
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label', `Agregar ${product.title}`);
          }
          this.updateSummary();
        };

        btn.addEventListener('click', toggle);
        grid.appendChild(card);
      });
    }

    /* ─── UI HELPERS ─── */

    makeMedia(imgSrc, alt) {
      const wrap = make('div', { className: 'mbts-card__media' });
      if (imgSrc) {
        const img = make('img', {
          className: 'mbts-card__img',
          attrs: {
            src: imgSrc, alt: alt || '',
            loading: 'lazy', width: '200', height: '200',
            draggable: 'false',
          },
        });
        img.addEventListener('contextmenu', (e) => e.preventDefault());
        wrap.appendChild(img);
      }
      return wrap;
    }

    makeCheck() {
      return make('span', {
        className: 'mbts-card__check',
        text: '✓',
        attrs: { 'aria-hidden': 'true' },
      });
    }

    makePrice(price, compareAt) {
      const wrap = make('div');
      if (compareAt && compareAt > price) {
        wrap.innerHTML = `<span class="mbts-card__price-compare">${formatPrice(compareAt)}</span><span class="mbts-card__price">${formatPrice(price)}</span>`;
      } else {
        wrap.innerHTML = `<span class="mbts-card__price">${formatPrice(price)}</span>`;
      }
      return wrap;
    }

    /** Asocia mouseenter/touchstart al card → tooltip con nombre */
    bindCardTooltip(card, name) {
      card.addEventListener('mouseenter', () => MbtsTooltip.show(card, name));
      card.addEventListener('mouseleave', () => MbtsTooltip.hide(80));
      card.addEventListener('focus',      () => MbtsTooltip.show(card, name));
      card.addEventListener('blur',       () => MbtsTooltip.hide(80));
      card.addEventListener('touchstart', () => MbtsTooltip.show(card, name), { passive: true });
      card.addEventListener('touchend',   () => MbtsTooltip.hide(1200), { passive: true });
    }

    /* ─── Current label en header del slot ─── */

    updateCurrentLabel(slotName, title, price, emptyText) {
      const map = {
        coches: '#mbts-current-coches',
        sillas: '#mbts-current-sillas',
        // sillasOpt comparte el slot de sillas (son exclusivas)
      };
      const target = this.root.querySelector(map[slotName]);
      if (!target) return;
      target.innerHTML = '';
      if (!title) {
        target.appendChild(make('span', {
          className: 'mbts-slot__current-empty',
          text: emptyText || 'Selecciona una opción',
        }));
        return;
      }
      target.appendChild(make('span', { className: 'mbts-slot__current-name', text: title }));
      if (price != null) {
        target.appendChild(make('span', {
          className: 'mbts-slot__current-price',
          text: formatPrice(price),
        }));
      }
    }

    /* ─── Stepper ─── */

    markStep(stepName) {
      this.completedSteps.add(stepName);
      this.syncStepperUI();
    }

    syncStepperUI(viewingSlot) {
      const steps = els('.mbts-stepper__step', this.root);

      steps.forEach((step) => {
        const id = step.getAttribute('data-mbts-step-indicator');
        step.classList.remove('is-active', 'is-done', 'is-viewing');
        if (this.completedSteps.has(id)) step.classList.add('is-done');
      });

      // Paso activo (siguiente pendiente)
      const order = ['coches', 'sillas', 'accesorios', 'resumen'];
      const nextPending = order.find(
        (s) =>
          !this.completedSteps.has(s) &&
          this.root.querySelector(`[data-mbts-step-indicator="${s}"]`)
      );
      if (nextPending) {
        const elNext = this.root.querySelector(`[data-mbts-step-indicator="${nextPending}"]`);
        elNext && elNext.classList.add('is-active');
      }

      // Resaltar el paso visible en scroll
      if (viewingSlot) {
        const elViewing = this.root.querySelector(`[data-mbts-step-indicator="${viewingSlot}"]`);
        if (elViewing && !elViewing.classList.contains('is-active')) {
          elViewing.classList.add('is-viewing');
        }
      }

      // Animar el track fill: igual que React ((highestIdx + 0.5) / (total-1)) * 100
      const fillEl = this.root.querySelector('.mbts-stepper__track-fill');
      if (fillEl && steps.length > 1) {
        let highestDone = -1;
        steps.forEach((step, idx) => {
          if (this.completedSteps.has(step.getAttribute('data-mbts-step-indicator'))) {
            highestDone = idx;
          }
        });
        const pct = highestDone >= 0
          ? Math.min(100, ((highestDone + 0.5) / (steps.length - 1)) * 100)
          : 0;
        fillEl.style.width = pct + '%';
      }

      // Borde animado + toast cuando coche Y silla están listos
      const sw = this.root.querySelector('.mbts-stepper-wrap');
      if (sw) {
        const bothDone = this.completedSteps.has('coches') && this.completedSteps.has('sillas');
        sw.classList.toggle('is-ready', bothDone);

        const doneToast = sw.querySelector('.mbts-stepper-done-toast');
        if (doneToast) {
          if (bothDone) {
            doneToast.classList.add('is-show');
          } else {
            doneToast.classList.remove('is-show');
          }
        }
      }
    }

    /* ─── Stepper: click para hacer scroll al paso ─── */

    bindStepperClicks() {
      els('.mbts-stepper__step', this.root).forEach((step) => {
        step.addEventListener('click', () => {
          const id = step.getAttribute('data-mbts-step-indicator');
          let target;
          if (id === 'resumen') {
            target = this.root.querySelector('#mbts-summary');
          } else {
            target = this.root.querySelector(`[data-mbts-slot="${id}"]`);
          }
          if (target) {
            const sw = this.root.querySelector('.mbts-stepper-wrap');
            const offset = sw ? sw.getBoundingClientRect().height + 16 : 130;
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        });
      });
    }

    /* ─── Stepper: scroll observer robusto (scroll + rAF) ─── */

    bindScrollObserver() {
      const slotIds = ['coches', 'sillas', 'accesorios', 'resumen'];
      const targets = slotIds.map((id) => {
        const el = id === 'resumen'
          ? this.root.querySelector('#mbts-summary')
          : this.root.querySelector(`[data-mbts-slot="${id}"]`);
        return el ? { el, id } : null;
      }).filter(Boolean);

      if (!targets.length) return;

      let rafPending = false;
      let lastViewing = null;

      const getActiveSlot = () => {
        const vh = window.innerHeight;
        // Punto de referencia: 45% desde arriba del viewport
        const refY = window.scrollY + vh * 0.45;

        let bestId = null;
        let bestScore = -Infinity;

        for (const { el, id } of targets) {
          const rect = el.getBoundingClientRect();
          // Ignorar elementos completamente fuera del viewport
          if (rect.bottom < 80 || rect.top > vh - 60) continue;

          const elTop  = window.scrollY + rect.top;
          const elMid  = elTop + rect.height / 2;
          // Mayor score → más cercano al punto de referencia
          const score  = -Math.abs(refY - elMid);

          if (score > bestScore) {
            bestScore = score;
            bestId = id;
          }
        }
        return bestId;
      };

      const update = () => {
        rafPending = false;
        const current = getActiveSlot();
        if (current === lastViewing) return;
        lastViewing = current;

        // Si el usuario llega al resumen, marcar accesorios como done (opcional)
        if (current === 'resumen' && !this.completedSteps.has('accesorios')) {
          this.completedSteps.add('accesorios');
        }

        this.syncStepperUI(current);
      };

      const onScroll = () => {
        if (!rafPending) {
          rafPending = true;
          requestAnimationFrame(update);
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      // Check inicial
      requestAnimationFrame(update);
    }

    /* ─── Summary ─── */

    updateSummary() {
      const list  = this.root.querySelector('#mbts-summary-list');
      const total = this.root.querySelector('#mbts-total');
      const cta   = this.root.querySelector('#mbts-cta');
      const hint  = this.root.querySelector('#mbts-cta-hint');
      const summaryWrap = this.root.querySelector('#mbts-summary');
      if (!list || !total) return;

      list.innerHTML = '';

      const addRow = (label, item, opts = {}) => {
        if (!item) return;

        const row = make('li', { className: 'mbts-summary__row' });

        // Thumbnail
        const thumb = make('div', {
          className: 'mbts-summary__row-thumb' + (item.image ? '' : ' mbts-summary__row-thumb--empty'),
        });
        if (item.image) {
          thumb.appendChild(make('img', { attrs: { src: item.image, alt: '', loading: 'lazy' } }));
        }
        row.appendChild(thumb);

        const nameCol = make('div', { className: 'mbts-summary__row-name' });
        nameCol.appendChild(make('span', { className: 'mbts-summary__row-label', text: label }));
        nameCol.appendChild(make('span', { className: 'mbts-summary__row-title', text: item.title }));
        row.appendChild(nameCol);

        const priceCol = make('span', {
          className: 'mbts-summary__row-price' + (opts.gift ? ' mbts-summary__row-price--gift' : ''),
        });
        if (opts.gift) {
          priceCol.innerHTML = `<small>${formatPrice(item.compareAtPrice || 0)}</small>Gratis`;
        } else {
          priceCol.textContent = formatPrice(item.price);
        }
        row.appendChild(priceCol);
        list.appendChild(row);
      };

      addRow('Coche', this.state.coche);
      addRow('Silla de auto', this.state.silla);
      this.state.adaptadores.forEach((a) => addRow('Adaptadores', a, { gift: a.isGift }));
      this.state.accesorios.forEach((a) => addRow('Accesorio', a));

      if (!list.children.length) {
        list.appendChild(make('li', {
          className: 'mbts-summary__empty',
          text: 'Aún no has seleccionado nada',
        }));
        summaryWrap && summaryWrap.classList.remove('has-items');
      } else {
        summaryWrap && summaryWrap.classList.add('has-items');
      }

      // Calcular total explícitamente desde state (adaptadores solo suman si no son gratis)
      const totalCents = (this.state.coche ? this.state.coche.price : 0)
        + (this.state.silla ? this.state.silla.price : 0)
        + this.state.adaptadores.reduce((s, a) => s + (a.price || 0), 0)
        + this.state.accesorios.reduce((s, a) => s + (a.price || 0), 0);

      total.textContent = formatPrice(totalCents);

      // Actualizar contador de artículos
      const countEl = this.root.querySelector('#mbts-summary-count');
      if (countEl) {
        const n = list.children.length;
        const hasEmpty = list.querySelector('.mbts-summary__empty');
        countEl.textContent = (!hasEmpty && n > 0)
          ? `${n} ${n === 1 ? 'artículo' : 'artículos'}`
          : '';
      }

      // Widget de cuotas (adaptadores suman solo si no son gratis)
      const hasCocheSlot = this.cfg.slots.coches.length > 0;
      const canShowCuotas = !!((this.state.coche || !hasCocheSlot) && this.state.silla);
      const cuotasCents = totalCents;
      this.updatePaymentWidget(canShowCuotas ? cuotasCents : 0);

      // CTA — si no hay coches con stock, se puede comprar solo con la silla
      const isReady = !!((this.state.coche || !hasCocheSlot) && this.state.silla);
      cta.disabled = !isReady;
      cta.setAttribute('aria-disabled', String(!isReady));

      // Marcar/desmarcar el paso "resumen" según si el CTA está listo
      if (isReady) {
        this.completedSteps.add('resumen');
      } else {
        this.completedSteps.delete('resumen');
      }
      this.syncStepperUI();

      if (hint) {
        if (isReady) {
          hint.textContent = '';
          hint.hidden = true;
        } else if ((this.state.coche || !hasCocheSlot) && !this.state.silla) {
          hint.textContent = 'Falta elegir la silla de auto';
          hint.hidden = false;
          hint.classList.remove('is-ready');
        } else if (!this.state.coche && hasCocheSlot && this.state.silla) {
          hint.textContent = 'Falta elegir el coche';
          hint.hidden = false;
          hint.classList.remove('is-ready');
        } else {
          hint.textContent = hasCocheSlot
            ? 'Selecciona un coche y silla para continuar'
            : 'Selecciona una silla para continuar';
          hint.hidden = false;
          hint.classList.remove('is-ready');
        }
      }
    }

    /* ─── Widget de cuotas (Ventipay / MercadoPago) ─── */

    updatePaymentWidget(totalCents) {
      const wrap = this.root.querySelector('#mbts-payment');
      const vp   = this.root.querySelector('#mbts-cuota-ventipay');
      const mp   = this.root.querySelector('#mbts-cuota-mp');
      if (!wrap) return;

      if (totalCents <= 0) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      vp && (vp.textContent = formatPrice(Math.round(totalCents / 3)));
      mp && (mp.textContent = formatPrice(Math.round(totalCents / 6)));
    }

    /* ═══════════════════════════════════════════
       CTA — agregar al carrito + drawer integration
       ═══════════════════════════════════════════ */

    bindCTA() {
      const btn   = this.root.querySelector('#mbts-cta');
      const error = this.root.querySelector('#mbts-error');
      if (!btn) return;

      btn.addEventListener('click', async () => {
        if (btn.disabled) return;
        btn.classList.add('is-loading');
        btn.disabled = true;
        error.hidden = true;

        const baseProps = {
          '_ts':    this.cfg.productTitle,
          '_ts_id': String(this.cfg.productId),
        };

        const items = [];
        if (this.state.coche) {
          items.push({
            id: this.state.coche.variantId,
            quantity: 1,
            properties: { ...baseProps, '_ts_slot': 'Coche' },
          });
        }
        items.push({
          id: this.state.silla.variantId,
          quantity: 1,
          properties: { ...baseProps, '_ts_slot': 'Silla' },
        });
        this.state.adaptadores.forEach((a) => {
          items.push({
            id: a.variantId,
            quantity: 1,
            properties: {
              ...baseProps,
              '_ts_slot': 'Adaptadores',
              ...(a.isGift ? { '_ts_gift': 'true' } : {}),
            },
          });
        });
        this.state.accesorios.forEach((a) => {
          items.push({
            id: a.variantId,
            quantity: 1,
            properties: { ...baseProps, '_ts_slot': 'Accesorio' },
          });
        });

        try {
          await this.cartAddMulti(items);

          // Feedback éxito
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          const labelEl = btn.querySelector('.mbts-cta__label');
          const spinnerEl = btn.querySelector('.mbts-cta__spinner');
          labelEl.textContent = 'Agregado al carrito';
          if (spinnerEl) {
            spinnerEl.insertAdjacentHTML('afterend', '<svg class="mbts-cta__check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>');
          }

          setTimeout(() => {
            btn.classList.remove('is-success');
            labelEl.textContent = 'Agregar al carrito';
            btn.querySelector('.mbts-cta__check')?.remove();
            btn.disabled = false;
          }, 2500);
        } catch (e) {
          console.error('[Minibee TS] add-to-cart error:', e);
          btn.classList.remove('is-loading');
          btn.disabled = false;
          error.textContent = e.message || 'Hubo un problema. Inténtalo de nuevo.';
          error.hidden = false;
        }
      });
    }

    /**
     * Agrega múltiples items al carrito usando el patrón del tema:
     * - routes.cart_add_url (si existe) o /cart/add.js
     * - fetchConfig() (si existe) para headers correctos
     * - sections: ['minicart-form'] para que Shopify devuelva el HTML renderizado
     * - Inyecta el HTML del minicart en el DOM
     * - Actualiza contadores, barra de envío gratis
     * - Abre el cart-notification (drawer) del tema
     */
    async cartAddMulti(items) {
      const url = (typeof routes !== 'undefined' && routes.cart_add_url)
        ? routes.cart_add_url
        : '/cart/add.js';

      const baseOpts = (typeof fetchConfig === 'function')
        ? fetchConfig()
        : {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json, text/javascript',
            },
          };

      const body = JSON.stringify({
        items,
        sections: ['minicart-form'],
        sections_url: window.location.pathname,
      });

      const res = await fetch(url, { ...baseOpts, body });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || 'No se pudo agregar al carrito');
      }
      const text = await res.text();
      const parsed = JSON.parse(text);

      // 1. Reemplazar el HTML del minicart con el renderizado por Shopify
      if (parsed.sections && parsed.sections['minicart-form']) {
        const minicartForm = document.getElementById('minicart-form');
        if (minicartForm) {
          const doc = new DOMParser().parseFromString(
            parsed.sections['minicart-form'],
            'text/html'
          );
          const inner = doc.querySelector('#minicart-form');
          if (inner) minicartForm.innerHTML = inner.innerHTML;
        }
      }

      // 2. Actualizar contadores y barra de envío gratis
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());

        document.querySelectorAll('.cart-count').forEach((el) => {
          el.innerHTML = el.classList.contains('cart-count-drawer')
            ? `(${cart.item_count})`
            : (cart.item_count > 100 ? '~' : cart.item_count);
        });

        const headerTotal = document.querySelector('header-total-price');
        if (headerTotal && typeof headerTotal.updateTotal === 'function') {
          headerTotal.updateTotal(cart);
        }

        const freeShip = document.querySelector('free-ship-progress-bar');
        if (freeShip && typeof freeShip.init === 'function') {
          freeShip.init(cart.items_subtotal_price);
        }
      } catch (e) {
        console.warn('[Minibee TS] No se pudo actualizar contadores:', e);
      }

      // 3. Abrir el drawer del tema (cart-notification)
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification) {
        if (typeof cartNotification.open === 'function') cartNotification.open();
        if (typeof cartNotification.cartAction === 'function') cartNotification.cartAction();
      }

      // 4. Re-init lazy load si existe
      if (typeof BlsLazyloadImg !== 'undefined' && BlsLazyloadImg.init) {
        BlsLazyloadImg.init();
      }

      // 5. Dispatch eventos por si otros componentes escuchan
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-update',  { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:added',   { bubbles: true, detail: { items } }));
    }
  }

  /* ─────────── Init ─────────── */
  const boot = () => {
    document.querySelectorAll('.mbts-configurator').forEach((node) => {
      if (node.dataset.mbtsBooted === '1') return;
      node.dataset.mbtsBooted = '1';
      new MinibeeTravelSystem(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();